// 줍기 알림 띠(D2a, 드롭 식별 회차): 화면 왼쪽 가장자리의 얇은 줄 최대 3개, 폭 min(240px, 게임 화면 28%, 최소 150px)·안전 영역 안(c015). 자리는 목표 카드·자동 버튼·연계 안내 줄을 피한다(place). 플레이 영역(영웅·앞길·위험물) 위에는 아무것도 띄우지 않는다(UI 규칙).
// 한 줄 = 아이템 그림(등급색 테두리) + 이름(등급색) + 종류 태그(장비·재료·보석) + 장착 중보다 좋으면 ▲. 같은 아이템을 연달아 주우면 새 줄 없이 ×N으로 쌓고,
// 방 정리 일괄 수거(c 2)는 종류별 한 줄로 묶는다. 전설·세트 장비가 떨어지는 순간은 위쪽 가운데 가는 배너(그림 없는 한 줄, 2.4초)로 알린다.
// Ev.LootPickup(장비 개체는 view.loot.picked[y − 1], 강화석 등급은 flags >> 1)과 Ev.LootDrop(b ≥ 3), 장비 연산 취소(Ev.GearNotice, c015)만 다룬다.
// 줄·배너 DOM은 처음에 만들어 돌려 쓴다(이벤트마다 새 노드를 만들지 않는다). 이름·그림·비교는 ui/dropInfo.ts.
import { Ev, type GameEvent } from '../shared/events';
import { Scene, combatScene, type GameView } from '../shared/types';
import { assetUrl } from '../pwa/assetMap';
import { FONT, el } from './dom';
import { LOOT_FEED_LAYOUT, gearNoticeText, lootFeedBoxWidthPx } from './gearModel';
import { viewH, viewRect, viewW } from '../shared/viewport';
import { Compare, DropKind, KIND_ICON, KIND_TAG, KIND_TAG_COLOR, compareToEquipped, gearDropInfo, gemDropInfo, gradeCssByIndex, gradeNameByIndex, stoneDropInfo, type DropInfo } from './dropInfo';
import { SLOT_ORDER } from '../shared/gearLedger';
import type { GearItem } from '../shared/gear';
import { NAMED_COLOR } from '../shared/content/items';

const ROWS = LOOT_FEED_LAYOUT.rows;
const SHOW_MS = 2600;
/** 전설·세트 드롭 배너 표시 시간(ms) */
const BANNER_MS = 2400;
/** 전설 배너 위쪽 위치(HUD 위쪽 띠 아래, 플레이 영역 위가 아닌 가는 줄) */
const BANNER_TOP = '52px';
const BULK_NAME = ['장비 수거', '강화석 수거', '보석 수거'] as const;
/** 줄 높이(배율 전, px)와 줄 사이 간격 */
const ROW_H = 28, ROW_GAP = 3;
/** 띠 위치를 다시 재는 간격(ms): 줄이 보이는 동안만, 연계 안내 줄·자동 이동 알약이 나타나 자리를 덮는 경우를 따라간다 */
const PLACE_EVERY_MS = 600;
/** 띠 아래 끝과 화면 아래 가장자리 사이 여백(px). 띠는 입력을 막지 않는다(pointer-events:none) */
const BOTTOM_KEEP = 40;
/** 띠가 피해야 할 왼쪽 띠 HUD(목표 카드·자동 버튼·자동 이동 알약·연계 안내 줄) */
const AVOID_SEL = '[data-testid="hud-objective"],[data-testid="hud-auto"],[data-testid="auto-status"],[data-testid="link-hint"],[data-testid="touch-joystick-idle"],[data-testid^="potion-"]:not([data-testid="potion-bar"]):not([data-testid*="count"])';

interface Row {
  readonly root: HTMLDivElement;
  readonly box: HTMLDivElement;
  readonly img: HTMLImageElement;
  readonly name: HTMLSpanElement;
  readonly cnt: HTMLSpanElement;
  readonly tag: HTMLSpanElement;
  readonly up: HTMLSpanElement;
  key: string;
  base: string;
  total: number;
  /** 일괄 수거 줄이 지금까지 본 가장 높은 등급 */
  grade: number;
  icons: readonly string[];
  iconTry: number;
}

export class LootFeed {
  private readonly box: HTMLDivElement;
  private readonly rows: Row[] = [];
  private readonly banner: HTMLDivElement;
  private readonly bannerGrade: HTMLSpanElement;
  private readonly bannerName: HTMLSpanElement;
  private bannerUntil = 0;
  private readonly until = new Float64Array(ROWS);
  /** 위치를 다시 잴 때가 됨(줄을 새로 쌓았거나 크기가 바뀜) */
  private placeDirty = true;
  private nextPlaceMs = 0;
  /** 지금 자리에 둘 수 있는 줄 수(1..ROWS) — 짧은 화면에서 목표 카드를 피하면 줄어든다 */
  private cap: number = ROWS;
  private lastCss = '';
  private avoidEls: Element[] = [];
  private topLeftEl: Element | null = null;
  private cursor: number;
  private kScale = 0;
  private now = 0;
  private readonly onEvent = (e: GameEvent): void => {
    if (e.type === Ev.LootPickup) this.onPickup(e);
    else if (e.type === Ev.LootDrop && e.a === 0 && (e.b >= 3 || (e.flags & 1) !== 0)) this.showBanner(e.b, e.c, (e.flags & 1) !== 0);
    else if (e.type === Ev.GearNotice && this.view.gear) {
      // 성공이라 보였던 장비 연산이 나중에 취소됨(c015): 마을에서도 띠를 잠깐 보여 조용히 사라지지 않게 한다
      const r = this.take();
      this.fill(r, '!notice' + e.tick, gearNoticeText(this.view.gear.notice, e.b).text, '#ff7a86', [], 1, KIND_ICON[0], '취소', '#ff7a86', Compare.None, false);
      this.noticeUntil = this.now + SHOW_MS * 2;
      this.until[this.rows.indexOf(r)] = this.noticeUntil;
    }
  };
  /** 장비 취소 알림을 보여 주는 동안(마을 포함 모든 화면에서 띠를 켠다) */
  private noticeUntil = 0;

  constructor(parent: HTMLElement, private readonly view: GameView) {
    this.box = el('div', 'position:absolute;left:env(safe-area-inset-left, 0px);top:44px;z-index:18;display:flex;flex-direction:column;gap:3px;pointer-events:none;transform-origin:0 0;' +
      'box-sizing:border-box;max-width:' + lootFeedBoxWidthPx(viewW() || 568) + 'px;', parent);
    this.box.dataset.testid = 'loot-feed';
    this.box.setAttribute('role', 'status');
    this.box.setAttribute('aria-live', 'polite');
    for (let i = 0; i < ROWS; i++) this.rows.push(this.makeRow());
    // 전설·세트 드롭 배너: 위쪽 가운데 가는 한 줄(HUD 위쪽 띠 아래). 처음엔 숨김
    this.banner = el('div', 'position:absolute;left:50%;top:' + BANNER_TOP + ';z-index:19;display:none;align-items:center;gap:8px;box-sizing:border-box;max-width:min(92vw,420px);padding:4px 16px;' +
      'border-radius:999px;pointer-events:none;opacity:0;transform:translateX(-50%) scale(.9);transition:opacity .22s,transform .22s;font:900 15px ' + FONT + ';color:#fff;' +
      'background:linear-gradient(90deg,rgba(40,28,6,.0),rgba(40,28,6,.86) 18%,rgba(40,28,6,.86) 82%,rgba(40,28,6,.0));text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;', parent);
    this.banner.dataset.testid = 'loot-banner';
    this.bannerGrade = el('span', 'flex:0 0 auto;letter-spacing:1px;', this.banner);
    this.bannerName = el('span', 'min-width:0;overflow:hidden;text-overflow:ellipsis;', this.banner);
    this.cursor = view.events.written;
    // 화면 크기·방향이 바뀌면 폭·자리를 다시 정한다(리사이즈 때만 레이아웃을 읽는다)
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('resize', this.onResize);
      window.addEventListener('orientationchange', this.onResize);
    }
  }

  private readonly onResize = (): void => { this.placeDirty = true; this.kScale = 0; };

  private makeRow(): Row {
    const root = el('div', 'box-sizing:border-box;max-width:100%;display:flex;align-items:center;gap:5px;padding:2px 10px 2px 3px;border-left:4px solid #fff;opacity:0;transition:opacity .25s;overflow:hidden;' +
      'background:linear-gradient(90deg,rgba(8,12,28,.86),rgba(8,12,28,.0));font:800 13px ' + FONT + ';color:#fff;text-shadow:0 1px 3px rgba(0,0,0,.9);', this.box);
    root.dataset.testid = 'loot-feed-row';
    const box = el('div', 'flex:0 0 auto;width:24px;height:24px;border-radius:6px;display:grid;place-items:center;box-sizing:border-box;border:1.5px solid #fff;background:rgba(8,12,26,.6);', root);
    box.dataset.testid = 'loot-feed-icon';
    const img = el('img', 'width:21px;height:21px;object-fit:contain;filter:drop-shadow(0 1px 2px rgba(0,0,0,.6));', box);
    img.alt = ''; img.decoding = 'async'; img.draggable = false;
    const name = el('span', 'flex:1 1 auto;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', root);
    name.dataset.testid = 'loot-feed-name';
    const cnt = el('span', 'flex:0 0 auto;font-weight:900;color:#fff;', root);
    const tag = el('span', 'flex:0 0 auto;font:900 10px ' + FONT + ';padding:0 5px;border-radius:5px;border:1px solid currentColor;line-height:14px;letter-spacing:.5px;background:rgba(0,0,0,.35);', root);
    tag.dataset.testid = 'loot-feed-tag';
    const up = el('span', 'flex:0 0 auto;font:900 12px ' + FONT + ';color:#6dff9a;display:none;', root, '▲');
    up.dataset.testid = 'loot-feed-up';
    const row: Row = { root, box, img, name, cnt, tag, up, key: '', base: '', total: 0, grade: 0, icons: [], iconTry: 0 };
    // 그림을 못 읽으면 다음 후보, 다 실패하면 칸을 비운다
    img.addEventListener('error', () => {
      if (row.iconTry + 1 < row.icons.length) { row.iconTry++; img.src = assetUrl(row.icons[row.iconTry]); } else box.style.visibility = 'hidden';
    });
    return row;
  }

  /**
   * 쓸 줄을 고른다: 이미 사라진 줄이 있으면 그것, 없으면 보이는 줄 중 사라질 시각(until)이 가장 이른 줄.
   * (예전에는 돌려 가며 골라, ×N으로 갱신한 줄이 먼저 덮였다 — PR #229 리뷰 P2) 고른 줄은 맨 아래로 올린다.
   * 위치는 여기서 재지 않는다: 이벤트를 다 처리한 뒤 update()가 한 번만 잰다(레이아웃 읽기 한 번)
   */
  private take(): Row {
    let k = 0, best = Infinity;
    for (let i = 0; i < this.cap; i++) {
      const u = this.until[i] > this.now ? this.until[i] : 0;
      if (u < best) { best = u; k = i; }
    }
    const r = this.rows[k];
    this.box.appendChild(r.root);
    this.until[k] = this.now + SHOW_MS;
    this.placeDirty = true;
    return r;
  }

  /**
   * 띠 자리(위쪽 y와 줄 수). 게임 화면 좌표(shared/viewport: 세로로 든 기기에서 #app을 돌려도 같은 좌표)로 잰다.
   * 기본은 왼쪽 위 가장자리(영웅 초상 바로 아래). 왼쪽 띠에 있는 목표 카드·자동 버튼·자동 이동 알약·연계 안내 줄과 겹치면 그 위·아래 후보 중 기본 자리에 가장 가까운 곳으로 옮긴다.
   * 3줄이 어디에도 안 들어가는 짧은 화면(568×320)에서는 줄 수를 2·1줄로 줄여 들어가는 자리를 찾는다. 위로는 영웅 초상 아래, 아래로는 조작 영역(BOTTOM_KEEP) 위까지.
   * 이벤트를 처리한 뒤·크기가 바뀐 뒤·줄이 보이는 동안 0.6초마다만 부른다(프레임마다 레이아웃을 읽지 않는다)
   */
  private place(): void {
    const W = viewW() || 568, H = viewH() || 320;
    const k = Math.max(LOOT_FEED_LAYOUT.minScale, this.kScale || 1);
    const bw = lootFeedBoxWidthPx(W), x0 = Math.max(0, viewRect(this.box).left), x1 = x0 + bw;
    if (this.avoidEls.length === 0 || !this.avoidEls[0].isConnected) this.avoidEls = Array.from(document.querySelectorAll(AVOID_SEL));
    if (!this.topLeftEl || !this.topLeftEl.isConnected) this.topLeftEl = document.querySelector('[data-testid="hud-top-left"]');
    const tl = this.topLeftEl ? viewRect(this.topLeftEl) : null;
    const lo = tl && tl.height > 0 ? Math.ceil(tl.bottom + 4) : 44;
    const hi = H - BOTTOM_KEEP;
    const obs: { t: number; b: number }[] = [];
    for (const el of this.avoidEls) {
      const r = viewRect(el);
      if (r.width > 0 && r.height > 0 && r.left < x1 && r.right > x0) obs.push({ t: r.top, b: r.bottom });
    }
    // 기본 자리 = 왼쪽 위 가장자리(영웅 초상 아래). 예전 40%(왼쪽 가운데)는 앞길·주인공 쪽으로 번져 폰에서 전투 한가운데와 겹쳤다(페르소나 QA ⑤)
    const def = lo;
    let top = def, cap: number = ROWS, found = false;
    for (let rows = ROWS; rows >= 1 && !found; rows--) {
      const bh = (rows * ROW_H + (rows - 1) * ROW_GAP) * k;
      // 후보: 기본 자리, 각 장애물의 아래·위
      let bestTop = -1, bestD = Infinity;
      for (let c = -3; c < obs.length * 2; c++) {
        const o = c < 0 ? null : obs[c >> 1];
        const y = c === -3 ? def : c === -2 ? lo : c === -1 ? hi - bh : c & 1 ? Math.floor(o!.t - 6 - bh) : Math.ceil(o!.b + 6);
        if (y < lo || y + bh > hi) continue;
        let hit = false;
        for (const b of obs) if (y < b.b + 4 && y + bh > b.t - 4) { hit = true; break; }
        if (hit) continue;
        const d = Math.abs(y - def);
        if (d < bestD) { bestD = d; bestTop = y; }
      }
      if (bestTop >= 0) { top = bestTop; cap = rows; found = true; }
    }
    if (!found) { top = Math.max(lo, Math.min(def, hi - ROW_H * k)); cap = 1; }
    if (cap !== this.cap) {
      this.cap = cap;
      for (let i = 0; i < ROWS; i++) {
        const hide = i >= cap;
        this.rows[i].root.style.display = hide ? 'none' : 'flex';
        if (hide) { this.until[i] = 0; this.rows[i].key = ''; this.rows[i].root.style.opacity = '0'; }
      }
    }
    const css = Math.round(top) + 'px';
    if (css !== this.lastCss) { this.lastCss = css; this.box.style.top = css; }
    const mw = (Math.round(bw / k)) + 'px';
    if (this.box.style.maxWidth !== mw) this.box.style.maxWidth = mw;
  }

  /** 줄 내용을 채운다. icons가 비면 종류 그림(catIcon)을 쓴다 */
  private fill(r: Row, key: string, base: string, color: string, icons: readonly string[], total: number, catIcon: string, tag: string, tagColor: string, cmp: number, big: boolean): void {
    r.key = key; r.base = base; r.total = total;
    r.root.style.borderLeftColor = color;
    r.root.style.opacity = '1';
    r.root.style.boxShadow = big ? 'inset 4px 0 10px -4px ' + color : 'none';
    r.box.style.borderColor = color;
    r.box.style.visibility = 'visible';
    r.name.style.color = color === '#e8e8e8' ? '#fff' : color;
    r.name.textContent = base;
    r.cnt.textContent = total > 1 ? '×' + total : '';
    r.tag.textContent = tag;
    r.tag.style.color = tagColor;
    r.up.style.display = cmp === Compare.Better || cmp === Compare.EmptySlot ? 'inline' : 'none';
    r.up.title = cmp === Compare.Better ? '장착 중보다 좋음' : cmp === Compare.EmptySlot ? '빈 칸에 장착할 수 있음' : '';
    r.icons = icons.length ? icons : [catIcon];
    r.iconTry = 0;
    r.img.src = assetUrl(r.icons[0]);
  }

  /** 이 키의 줄이 아직 보이면 그 줄 번호, 없으면 -1 */
  private findLive(key: string): number {
    for (let i = 0; i < ROWS; i++) if (this.until[i] > this.now && this.rows[i].key === key) return i;
    return -1;
  }

  private onPickup(e: GameEvent): void {
    const kind = e.a, bulk = e.c === 2, pet = e.c === 1, salvaged = (e.flags & 1) !== 0;
    let info: DropInfo;
    let amount = 1; // 강화석은 개수를 더한다
    let item: GearItem | null = null;
    if (kind === DropKind.Stone) { info = stoneDropInfo(e.flags >> 1, 1); amount = Math.max(1, e.d | 0); }
    else if (kind === DropKind.Gem) info = gemDropInfo(e.d, e.b);
    else { item = e.y > 0 ? this.view.loot.picked[e.y - 1] ?? null : null; info = gearDropInfo(item, e.b, e.d); }
    // 같은 아이템 묶음 키: 장비 = 정의+등급, 강화석 = 등급, 보석 = 코드(+ 자동 정리·펫 여부: 줄 이름에 '· 펫'이 붙어 섞이면 ×N이 거짓이 된다). 일괄 수거는 종류별 한 줄
    const key = bulk ? 'bulk' + kind : kind === DropKind.Gear ? 'g:' + (item ? item.def : 'x' + e.d) + ':' + e.b + (salvaged ? 's' : '') + (pet ? 'p' : '') : kind === DropKind.Stone ? 's:' + (e.flags >> 1) + (pet ? 'p' : '') : 'm:' + e.d + (pet ? 'p' : '');
    const i = this.findLive(key);
    if (i >= 0) {
      // 쌓기: 개수만 올리고 시간을 늘리며 맨 아래로 올린다
      const r = this.rows[i];
      r.total += amount;
      r.cnt.textContent = '×' + r.total;
      this.box.appendChild(r.root);
      this.until[i] = this.now + SHOW_MS;
      if (bulk && kind === DropKind.Gear && e.b > r.grade) this.recolor(r, e.b);
      return;
    }
    const cmp = item && !salvaged && !bulk ? compareToEquipped(item, this.equippedOf(e.d), this.view.heroId, this.equippedOthers(e.d)) : Compare.None;
    const r = this.take();
    const tag = salvaged ? '정리' : KIND_TAG[kind];
    const tagColor = salvaged ? '#9aa3b5' : KIND_TAG_COLOR[kind];
    const base = bulk ? BULK_NAME[kind] : info.name + (pet ? ' · 펫' : '');
    const color = bulk && kind === DropKind.Gear ? gradeCssByIndex(e.b) : info.color;
    this.fill(r, key, base, color, bulk ? [] : info.icons, amount, KIND_ICON[kind], tag, tagColor, cmp, kind === DropKind.Gear && e.b >= 3);
    r.grade = e.b;
  }

  private recolor(r: Row, grade: number): void {
    const c = gradeCssByIndex(grade);
    r.grade = grade;
    r.root.style.borderLeftColor = c; r.box.style.borderColor = c; r.name.style.color = c;
  }

  /** 지금 장착한 같은 부위 장비(없으면 null). slotIdx = gearLedger.SLOT_ORDER 번호 */
  private equippedOf(slotIdx: number): GearItem | null {
    const g = this.view.gear;
    if (!g || slotIdx < 0 || slotIdx >= SLOT_ORDER.length) return null;
    const uid = g.equipped[SLOT_ORDER[slotIdx]];
    if (!uid) return null;
    const its = g.items;
    for (let k = 0; k < its.length; k++) if (its[k].uid === uid) return its[k];
    return null;
  }

  /** 장착 중인 나머지 부위(세트 효과 비교용, 이벤트당 한 번만 만든다) */
  private equippedOthers(slotIdx: number): GearItem[] {
    const g = this.view.gear, out: GearItem[] = [];
    if (!g) return out;
    for (let s = 0; s < SLOT_ORDER.length; s++) {
      if (s === slotIdx) continue;
      const uid = g.equipped[SLOT_ORDER[s]];
      if (!uid) continue;
      const it = g.items.find((x) => x.uid === uid);
      if (it) out.push(it);
    }
    return out;
  }

  /** 전설(4)·세트(3) 장비가 바닥에 떨어진 순간의 배너 */
  /** named = 이름 있는 유니크(LootDrop flags & 1): 등급(영웅)과 관계없이 주황·큰 글씨로 '유니크 발견!' */
  private showBanner(grade: number, slot: number, named = false): void {
    const item = this.view.loot.item[slot] ?? null;
    const c = named ? NAMED_COLOR : gradeCssByIndex(grade);
    this.bannerGrade.textContent = named ? '✦ 유니크 발견!' : (grade === 4 ? '★ ' : '◆ ') + gradeNameByIndex(grade) + ' 장비 발견!';
    this.bannerGrade.style.color = c;
    // 같은 프레임에 펫이 먼저 주웠다면 item이 비어 있다: 등급 이름으로 대신한다
    this.bannerName.textContent = gearDropInfo(item, grade, -1).name;
    this.banner.style.borderTop = '1.5px solid ' + c; this.banner.style.borderBottom = '1.5px solid ' + c;
    this.banner.style.boxShadow = '0 0 14px -2px ' + c;
    this.banner.style.fontSize = grade === 4 || named ? '16px' : '14px';
    this.banner.style.display = 'flex';
    // 표시 시작: 다음 프레임에 투명도·크기를 올려 전환이 걸리게 한다
    this.banner.style.opacity = '0'; this.banner.style.transform = 'translateX(-50%) scale(.9)';
    void this.banner.offsetWidth;
    this.banner.style.opacity = '1'; this.banner.style.transform = 'translateX(-50%) scale(1)';
    this.bannerUntil = this.now + BANNER_MS;
  }

  update(u: number, nowMs: number): void {
    this.now = nowMs;
    const s = this.view.flow.scene;
    this.cursor = this.view.events.readFrom(this.cursor, this.onEvent);
    const on = combatScene(s) || s === Scene.Result || nowMs < this.noticeUntil;
    this.box.style.display = on ? 'flex' : 'none';
    if (u !== this.kScale) {
      this.kScale = u;
      this.box.style.transform = 'scale(' + Math.max(LOOT_FEED_LAYOUT.minScale, u).toFixed(3) + ')';
      // 배율로 커져도 화면상 폭이 같게 max-width는 place()가 배율로 나눠 정한다. 배너 글자 크기(등급별 16·14px)는 건드리지 않는다
      this.placeDirty = true;
    }
    for (let i = 0; i < ROWS; i++) if (this.until[i] > 0 && nowMs > this.until[i]) { this.until[i] = 0; this.rows[i].root.style.opacity = '0'; this.rows[i].key = ''; }
    // 띠 자리: 이벤트를 다 처리한 뒤 한 번만(레이아웃 읽기). 줄이 보이는 동안은 0.6초마다 다시 재서 나중에 나타난 HUD를 피한다
    if (on) {
      let live = false;
      for (let i = 0; i < this.cap; i++) if (this.until[i] > nowMs) { live = true; break; }
      if (this.placeDirty || (live && nowMs >= this.nextPlaceMs)) { this.placeDirty = false; this.nextPlaceMs = nowMs + PLACE_EVERY_MS; this.place(); }
    }
    if (this.bannerUntil > 0 && (nowMs > this.bannerUntil || !combatScene(s))) {
      this.bannerUntil = 0;
      this.banner.style.opacity = '0';
      this.banner.style.display = 'none';
    }
  }
}
